import * as React from "react";
import Markdown from "markdown-to-jsx";
import removeMarkdown from "remove-markdown";
import startCase from "lodash.startcase";
import { IAiChatMessage, IAiTableData } from "./AiSearchTypes";

export const stripCitationMarkers = (text: string) => {
    const unescapedText = String(text || "").replace(/\\([[\]])/g, "$1");
    return unescapedText
        .replace(/\[+\d+\]+\(https?:\/\/[^)]+\)\]*/g, "")
        .replace(/\[\d+\]/g, "")
        .replace(/[ \t]{2,}/g, " ")
        .trim();
};

export const normalizeResponse = (response: any, sourceDataSource?: string) => {
    const textContent = stripCitationMarkers(response?.textContent ?? response?.TextContent ?? "");
    const suggestedQuestions = response?.suggestedQuestions ?? response?.SuggestedQuestions ?? [];
    const tabularData = response?.tabularData ?? response?.TabularData ?? [];
    const aiTables: IAiTableData[] = Array.isArray(tabularData)
        ? tabularData
            .map((item: any) => ({
                statementId: item?.statementId ?? item?.StatementId,
                dataArray: item?.dataArray ?? item?.DataArray,
                columnNames: (item?.columnNames ?? item?.ColumnNames ?? []).map((columnName: any) => {
                    const raw = String(columnName || "").trim();
                    if (!raw) {
                        return raw;
                    }

                    return startCase(raw)
                        .replace(/\bId\b/g, "ID")
                        .replace(/\bIswc\b/g, "ISWC");
                }),
                query: item?.query ?? item?.Query,
                sourceDataSource
            }))
            .filter((item: IAiTableData) => Array.isArray(item.dataArray) && item.dataArray.length > 0)
        : [];

    return {
        textContent: textContent || "",
        suggestedQuestions: Array.isArray(suggestedQuestions) ? suggestedQuestions : [],
        aiTables
    };
};

export const updateMessage = (messages: IAiChatMessage[], messageId: string, updates: Partial<IAiChatMessage>) => {
    return (messages || []).map((message) =>
        message.id === messageId
            ? { ...message, ...updates }
            : message
    );
};

export const renderAssistantMarkdown = (text?: string) => {
    if (!text) {
        return null;
    }

    return React.createElement(Markdown as React.ElementType, {
        options: {
            forceBlock: true,
            overrides: {
                a: {
                    props: {
                        target: "_blank",
                        rel: "noopener noreferrer"
                    }
                }
            }
        }
    }, String(text));
};

export const stripMarkdownDecorators = (text: string) => {
    return removeMarkdown(String(text || "")).trim();
};

export const getFunctionCallTitle = (item: any) => {
    if (!item) {
        return "";
    }

    try {
        const args = item?.arguments;
        if (typeof args === "string" && args.trim()) {
            const parsed = JSON.parse(args);
            if (parsed?.title) {
                return String(parsed.title).trim();
            }
        } else if (args?.title) {
            return String(args.title).trim();
        }
    } catch {
        // Ignore malformed arguments JSON and fallback to output text parsing.
    }

    const outputText = typeof item?.output === "string" ? item.output : "";
    const firstLine = outputText.split("\n").find((line: string) => line.trim().length > 0) || "";
    return stripMarkdownDecorators(firstLine);
};

export const getStreamStatusMessage = (streamEvent: any) => {
    const eventType = streamEvent?.type;
    const eventData = streamEvent?.data;
    const item = eventData?.item;

    if (eventType !== "response.output_item.added" && eventType !== "response.output_item.updated" && eventType !== "response.output_item.done") {
        return "";
    }

    if (item?.type === "reasoning") {
        const reasoningText = Array.isArray(item?.content)
            ? item.content.find((contentItem: any) => contentItem?.type === "reasoning_text")?.text
            : "";

        if (typeof reasoningText === "string" && reasoningText.trim()) {
            return stripCitationMarkers(stripMarkdownDecorators(reasoningText));
        }
    }

    if (item?.type === "function_call") {
        const title = getFunctionCallTitle(item);
        return stripCitationMarkers(title);
    }

    if (item?.type === "function_call_output") {
        const title = getFunctionCallTitle(item);
        if (item?.status === "in_progress" || item?.status === "completed") {
            return stripCitationMarkers(title);
        }
    }

    return "";
};

export const getAiTableKey = (table: IAiTableData, index: number) => table?.statementId || `table-${index}`;

export const findTitleColumnIndex = (table: IAiTableData): number => {
    return (table?.columnNames || []).findIndex((name: string) => String(name || "").toLowerCase().includes("title"));
};

interface IAiPackageActionTableInfo {
    tableKey: string;
    workIdsByRowIndex: (number | undefined)[];
    titlesByRowIndex: (string | undefined)[];
}

export const findPackageActionWorkIdColumnIndex = (table: IAiTableData, pageName?: string): number => {
    const isWorksContext = (pageName || "").toLowerCase().includes("work");
    if (!isWorksContext) {
        return -1;
    }

    const isRepertoireDataSource = (table?.sourceDataSource || "").toLowerCase() === "repertoire";
    if (!isRepertoireDataSource) {
        return -1;
    }

    return (table?.columnNames || []).findIndex((name: string) => {
        const normalized = String(name || "").toLowerCase().trim();
        return normalized === "work id" || normalized === "id" || normalized === "workid";
    });
};

export const findUsageIdColumnIndex = (table: IAiTableData, pageName?: string): number => {
    const isUsagesContext = (pageName || "").toLowerCase().includes("usage");
    if (!isUsagesContext) {
        return -1;
    }

    return (table?.columnNames || []).findIndex((name: string) => {
        const normalized = String(name || "").toLowerCase().trim();
        return normalized === "usage id" || normalized === "id" || normalized === "usageid"
            || normalized === "usage group id" || normalized === "usagegroupid";
    });
};

const getPackageActionTables = (tables: IAiTableData[] | undefined, pageName?: string): IAiPackageActionTableInfo[] => {
    const packageActionTables: IAiPackageActionTableInfo[] = [];

    (tables || []).forEach((table, index) => {
        const workIdColumnIndex = findPackageActionWorkIdColumnIndex(table, pageName);
        if (workIdColumnIndex < 0) {
            return;
        }

        const titleColumnIndex = findTitleColumnIndex(table);

        packageActionTables.push({
            tableKey: getAiTableKey(table, index),
            workIdsByRowIndex: (table.dataArray || []).map((row: any) => {
                const workId = Number(Array.isArray(row) ? row[workIdColumnIndex] : undefined);
                return Number.isFinite(workId) && workId > 0 ? workId : undefined;
            }),
            titlesByRowIndex: (table.dataArray || []).map((row: any) => {
                const value = titleColumnIndex >= 0 && Array.isArray(row) ? row[titleColumnIndex] : undefined;
                const title = value === undefined || value === null ? "" : String(value).trim();
                return title || undefined;
            })
        });
    });

    return packageActionTables;
};

export interface IAiPackageActionItem {
    workId: number;
    title?: string;
}

export const getSelectedPackageActionWorkItems = (
    tables: IAiTableData[] | undefined,
    pageName: string | undefined,
    selection: { [tableKey: string]: (string | number)[] } | undefined
): IAiPackageActionItem[] => {
    const items = new Map<number, string | undefined>();

    getPackageActionTables(tables, pageName).forEach(({ tableKey, workIdsByRowIndex, titlesByRowIndex }) => {
        const rowIndexes = (selection?.[tableKey] || []).map(Number);

        rowIndexes.forEach(rowIndex => {
            const workId = workIdsByRowIndex[rowIndex];
            if (workId !== undefined && !items.has(workId)) {
                items.set(workId, titlesByRowIndex[rowIndex]);
            }
        });
    });

    return Array.from(items.entries()).map(([workId, title]) => ({ workId, title }));
};

interface IAiUsageActionTableInfo {
    tableKey: string;
    usageIdsByRowIndex: (string | undefined)[];
    titlesByRowIndex: (string | undefined)[];
}

const getUsageActionTables = (tables: IAiTableData[] | undefined, pageName?: string): IAiUsageActionTableInfo[] => {
    const usageActionTables: IAiUsageActionTableInfo[] = [];

    (tables || []).forEach((table, index) => {
        const usageIdColumnIndex = findUsageIdColumnIndex(table, pageName);
        if (usageIdColumnIndex < 0) {
            return;
        }

        const titleColumnIndex = findTitleColumnIndex(table);

        usageActionTables.push({
            tableKey: getAiTableKey(table, index),
            usageIdsByRowIndex: (table.dataArray || []).map((row: any) => {
                const value = Array.isArray(row) ? row[usageIdColumnIndex] : undefined;
                const usageId = value === undefined || value === null ? "" : String(value).trim();
                return usageId || undefined;
            }),
            titlesByRowIndex: (table.dataArray || []).map((row: any) => {
                const value = titleColumnIndex >= 0 && Array.isArray(row) ? row[titleColumnIndex] : undefined;
                const title = value === undefined || value === null ? "" : String(value).trim();
                return title || undefined;
            })
        });
    });

    return usageActionTables;
};

export interface IAiUsageActionItem {
    usageId: string;
    title?: string;
}

export const getPackageActionWorkItems = (tables: IAiTableData[] | undefined, pageName?: string): IAiPackageActionItem[] => {
    const items = new Map<number, string | undefined>();

    getPackageActionTables(tables, pageName).forEach(({ workIdsByRowIndex, titlesByRowIndex }) => {
        workIdsByRowIndex.forEach((workId, rowIndex) => {
            if (workId !== undefined && !items.has(workId)) {
                items.set(workId, titlesByRowIndex[rowIndex]);
            }
        });
    });

    return Array.from(items.entries()).map(([workId, title]) => ({ workId, title }));
};

export const getSelectedUsageActionItems = (
    tables: IAiTableData[] | undefined,
    pageName: string | undefined,
    selection: { [tableKey: string]: (string | number)[] } | undefined
): IAiUsageActionItem[] => {
    const items = new Map<string, string | undefined>();

    getUsageActionTables(tables, pageName).forEach(({ tableKey, usageIdsByRowIndex, titlesByRowIndex }) => {
        const rowIndexes = (selection?.[tableKey] || []).map(Number);

        rowIndexes.forEach(rowIndex => {
            const usageId = usageIdsByRowIndex[rowIndex];
            if (usageId !== undefined && !items.has(usageId)) {
                items.set(usageId, titlesByRowIndex[rowIndex]);
            }
        });
    });

    return Array.from(items.entries()).map(([usageId, title]) => ({ usageId, title }));
};

export const getUsageActionItems = (tables: IAiTableData[] | undefined, pageName?: string): IAiUsageActionItem[] => {
    const items = new Map<string, string | undefined>();

    getUsageActionTables(tables, pageName).forEach(({ usageIdsByRowIndex, titlesByRowIndex }) => {
        usageIdsByRowIndex.forEach((usageId, rowIndex) => {
            if (usageId !== undefined && !items.has(usageId)) {
                items.set(usageId, titlesByRowIndex[rowIndex]);
            }
        });
    });

    return Array.from(items.entries()).map(([usageId, title]) => ({ usageId, title }));
};
